<template>
  <BaseNewForm class="noprogress">
    <template v-slot:header>
      {{ $t('car_vehicle_value_header') }}
    </template>
    <p class="vehicle-value__desc" v-if="hasRangeProps">{{ $t('car_vehicle_value_desc') }}</p>
    <div class="inputList">
      <BaseInput
        :placeholder="$t('car_vehicle_value_input')"
        :validation="$v.vehicleValue"
        v-model="vehicleValue"
        :thousandSeparator="true"
        @blur="checkValue"
        inputMode="decimal"
        class="contact-form vehicle-value-input"
        :class="{ shake: needAttention }"
        inputStyle="big"
      >
      <template v-if="!$v.vehicleValue.minValue" #invalid-input-error>
        {{ $t('car_vehicle_value_min_value_error', { minValue: $options.filters.splitNumber(minValue) }) }}
      </template>
    </BaseInput>

      <RangeInput
        v-if="hasRangeProps"
        v-model="vehicleValue"
        :min="minValue"
        :max="maxValue"
        :error="rangeError"
      />
      <div class="declared-count">
        <img src="@/assets/images/car-value.svg" alt="icon" />
        <div class="declared-count__info">
          <p>
            <template v-if="hasRangeProps">
              {{
                $t('car_vehicle_value_medium_value', {
                  carTitle,
                  minValue: $options.filters.splitNumber(minValue),
                  maxValue: $options.filters.splitNumber(maxValue),
                  mediumValue: $options.filters.splitNumber(mediumValue),
                })
              }}
            </template>
            <template v-else>
              {{ $t('car_vehicle_value_medium_value', { carTitle, minValue: $options.filters.splitNumber(minValue) }) }}
            </template>
          </p>
        </div>
      </div>
    </div>
    <BaseButton @click="onSubmit" />
    <p class="vehicle-value__contact">
      <a href="" @click.prevent="$ee.$emit('showContactUs')">{{
        $t('car_vehicle_value_contact_us.term1')
      }}</a>
      {{ $t('car_vehicle_value_contact_us.term2') }}
    </p>
  </BaseNewForm>
</template>

<script>
import { required, minValue } from 'vuelidate/lib/validators'
import { wizardStepMixin, BaseNewForm } from 'qic-components'
import RangeInput from '@/components/RangeInput.vue'
import { trackEvent } from '@/utils/useGtag'

const int11 = 11

export default {
  name: 'PlanVehicleValue',
  mixins: [
    wizardStepMixin
  ],
  components: {
    BaseNewForm,
    RangeInput
  },
  data () {
    return {
      vehicleValue: null,
      mediumValue: null,
      minValue: null,
      maxValue: null,
      needAttention: false,
      rangeError: null
    }
  },
  computed: {
    policyInfo () {
      return this.$store.state.renew.policyInfo
    },
    carInfo () {
      if (!this.policyInfo) return {}
      return {
        makeModel: this.policyInfo.makeModel,
        modelYear: this.policyInfo.modelYear
      }
    },
    submitDisabled () {
      return this.$v.$invalid
    },
    carTitle () {
      return `${this.carInfo.makeModel} ${this.carInfo.modelYear}`
    },
    hasRangeProps () {
      return this.mediumValue && this.minValue && this.maxValue
    }
  },
  methods: {
    onSubmit () {
      this.$v.vehicleValue.$touch()
      if (this.submitDisabled || this.needAttention) return
      this.$store.commit('setVehicleValue', this.vehicleValue)
      this.$emit('next')
    },
    checkValue () {
      if (!this.hasRangeProps) {
        if (isFinite(+this.vehicleValue) && !(this.vehicleValue.length > int11)) {
          this.$store.commit('setVehicleValue', this.vehicleValue)
        }
        this.$v.vehicleValue.$touch()
        return
      }

      if (
        +this.vehicleValue >= +this.minValue &&
        +this.vehicleValue <= +this.maxValue &&
        isFinite(+this.vehicleValue) &&
        !(this.vehicleValue.length > int11)
      ) {
        this.$store.commit('setVehicleValue', this.vehicleValue)
      }
      if (+this.vehicleValue < +this.minValue) {
        this.shakeInput()
        this.setRangeLimitError('min')
        return (this.vehicleValue = this.minValue)
      }
      if (+this.vehicleValue > +this.maxValue) {
        this.shakeInput()
        this.setRangeLimitError('max')
        return (this.vehicleValue = this.maxValue)
      }
      trackEvent('typeCarValue', this.vehicleValue.toString())
    },
    shakeInput () {
      this.needAttention = true
      setTimeout(() => (this.needAttention = false), 1000)
    },
    setRangeLimitError (side) {
      this.rangeError = side
      setTimeout(() => (this.rangeError = null), 3000)
    }
  },
  watch: {
    vehicleValue (newValue, oldValue) {
      if (!isFinite(+newValue)) this.vehicleValue = this.mediumValue
      if (newValue.length > int11) this.vehicleValue = oldValue
    }
  },
  mounted () {
    const policyInfo = this.$store.state.renew.policyInfo
    const vehicleValue = this.$store.state.motor.vehicleValue
    this.minValue = vehicleValue
    this.load(
      this.$store.dispatch('getCarValueXA', policyInfo?.chassisNo)
        .then(({ vehicleValues: values, minCarValue }) => {
          if (!values) {
            this.vehicleValue = vehicleValue && vehicleValue > minCarValue
              ? vehicleValue
              : minCarValue
            this.minValue = minCarValue
            return
          }
          const { high, medium, low } = values

          this.mediumValue = medium
          this.minValue = low
          this.maxValue = high

          this.vehicleValue = (vehicleValue > high || vehicleValue < low)
            ? medium
            : vehicleValue
        })
        .catch(() => {
          this.vehicleValue = vehicleValue
          return Promise.resolve()
        })
    )
  },
  validations () {
    const validations = {
      vehicleValue: {
        required
      }
    }

    if (!this.hasRangeProps) {
      validations.vehicleValue.minValue = minValue(this.minValue)
    }

    return validations
  }
}
</script>

<style lang="scss">
.vehicle-value {
  &__desc {
    text-align: center;
    margin: 0 auto 16px;
    max-width: 416px;

    font-size: 16px;
    line-height: 24px;

    @media (max-width: 550px){
      font-size: 14px;
      line-height: 24px;
    }
  }
  &__contact{
    margin-top: 24px;
    font-size: 16px;
    line-height: 24px;
    color: #1C1C1C;
    a{
      color: #5927FF;
    }
    text-align: center;
    @media (max-width: 550px){
      font-size: 14px;
      line-height: 24px;
    }
  }
}

.declared-count {
  padding: 24px;
  @media (max-width: 550px) {
    padding: 16px;
  }
  &__info {
    display: grid;
    gap: 16px;
    a {
      color: rgba(89, 39, 255, 1);
    }
  }
}

.vehicle-value-input.shake {
  animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  perspective: 1000px;
}

@keyframes shake {
  10%,
  90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(2px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(4px, 0, 0);
  }
}
</style>
